// apps/client/src/pages/leads/website/WebsiteHealthPanel.tsx
//
// 30-day health for one website connection: submissions per form (chart),
// rejections by reason, welcome email outcomes, last submission, and the
// paused-welcome banner with Resume (super_admin only).
//
// Counts only: the stats docs carry no personal information, and nothing
// here can be downloaded (no-data-export rule).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, MailWarning, PlayCircle, ShieldAlert } from 'lucide-react';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { Spinner } from '../../../components/common/Spinner';
import {
  HEALTH_WINDOW_DAYS,
  summariseHealth,
  type WebsiteConnectionHealth,
  type WebsiteConnectionView,
} from '../../../hooks/useWebsiteConnections';
import { FORM_LABELS, rejectionLabel, welcomePausedLabel } from './websiteConnectionForm';

interface Props {
  connection: WebsiteConnectionView;
  canWrite: boolean;
  loadHealth: (connectionId: string) => Promise<WebsiteConnectionHealth>;
  onResumeWelcome: (connectionId: string) => Promise<void>;
  /** Bumped by the page to force a reload (header refresh). */
  reloadKey: number;
}

const FORM_COLOURS = { enquiry: '#3b82f6', newsletter: '#10b981', referral: '#f59e0b' } as const;

function formatWhen(ms: number | null): string {
  if (ms === null) return 'No submissions yet';
  return new Date(ms).toLocaleString('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function pct(part: number, whole: number): string {
  if (whole <= 0) return '0%';
  return `${((part / whole) * 100).toFixed(part / whole < 0.01 && part > 0 ? 2 : 1)}%`;
}

export const WebsiteHealthPanel: React.FC<Props> = ({ connection, canWrite, loadHealth, onResumeWelcome, reloadKey }) => {
  const [health, setHealth] = useState<WebsiteConnectionHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setHealth(await loadHealth(connection.id));
    } catch (err) {
      setHealth(null);
      setError(err instanceof Error ? err.message : 'Health could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [connection.id, loadHealth]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadHealth(connection.id)
      .then((h) => {
        if (!cancelled) setHealth(h);
      })
      .catch((err) => {
        if (cancelled) return;
        setHealth(null);
        setError(err instanceof Error ? err.message : 'Health could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connection.id, loadHealth, reloadKey]);

  const summary = useMemo(() => (health ? summariseHealth(health) : null), [health]);
  const chartData = useMemo(
    () =>
      (health?.days ?? []).map((d) => ({
        day: `${Number(d.date.slice(8, 10))}/${Number(d.date.slice(5, 7))}`,
        enquiry: d.submissions.enquiry,
        newsletter: d.submissions.newsletter,
        referral: d.submissions.referral,
      })),
    [health],
  );

  const pausedReason = connection.welcome?.pausedReason ?? null;

  const handleResume = async () => {
    setResuming(true);
    setResumeError(null);
    try {
      await onResumeWelcome(connection.id);
    } catch (err) {
      setResumeError(err instanceof Error ? err.message : 'Welcome emails were not resumed.');
    } finally {
      setResuming(false);
    }
  };

  return (
    <div className="space-y-4">
      {pausedReason && (
        <div role="alert" className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <MailWarning className="w-5 h-5 text-amber-300 flex-shrink-0" />
          <div className="flex-1 text-sm text-amber-200">
            <p className="font-medium">Welcome emails are paused</p>
            <p className="text-amber-200/80">
              VALUTTEN paused them because {welcomePausedLabel(pausedReason)}. New subscribers are still added; they just do not get a welcome email.
              Check the website form and your sending domain before resuming.
            </p>
            {resumeError && <p className="text-red-300 mt-1">{resumeError}</p>}
          </div>
          {canWrite && (
            <button type="button" onClick={handleResume} disabled={resuming}
              className="px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-700 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
              {resuming ? <Spinner size="sm" /> : <PlayCircle className="w-4 h-4" />}
              Resume
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-3" aria-label="Loading health">
          <Skeleton height="16rem" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton height="8rem" />
            <Skeleton height="8rem" />
          </div>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={load} className="text-xs underline">Retry</button>
        </div>
      ) : health && summary ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(['enquiry', 'newsletter', 'referral'] as const).map((f) => (
              <div key={f} className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
                <p className="text-xs uppercase tracking-wider text-gray-400">{FORM_LABELS[f]} submissions</p>
                <p className="text-2xl font-semibold mt-1">{summary.submissions[f].toLocaleString('en-AU')}</p>
                <p className="text-xs text-gray-500">Last {HEALTH_WINDOW_DAYS} days</p>
              </div>
            ))}
            <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
              <p className="text-xs uppercase tracking-wider text-gray-400">Last submission</p>
              <p className="text-sm font-medium mt-2">{formatWhen(health.lastSubmissionAtMs)}</p>
              <p className="text-xs text-gray-500">Brisbane time</p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
            <div className="text-[0.6875rem] uppercase tracking-wider text-gray-400 mb-3">
              Accepted submissions per day
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
                  <CartesianGrid stroke="#374151" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: '#9ca3af', fontSize: 11 }} stroke="#374151" interval="preserveStartEnd" />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} stroke="#374151" allowDecimals={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(37,99,235,0.08)' }}
                    contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8 }}
                    labelStyle={{ color: '#e5e7eb' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {(['enquiry', 'newsletter', 'referral'] as const).map((f) => (
                    <Bar key={f} dataKey={f} name={FORM_LABELS[f]} stackId="forms" fill={FORM_COLOURS[f]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold">Refused or flagged</h3>
              </div>
              {summary.rejections.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-400 text-center">Nothing refused or flagged in the last {HEALTH_WINDOW_DAYS} days.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400">
                      <th className="text-left font-medium px-4 py-2">Reason</th>
                      <th className="text-right font-medium px-4 py-2">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.rejections.map((r) => (
                      <tr key={r.reason} className="border-t border-gray-700/60">
                        <td className="px-4 py-2">{rejectionLabel(r.reason)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{r.count.toLocaleString('en-AU')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {connection.mode !== 'enforce' && summary.rejections.length > 0 && (
                <p className="px-4 py-2 text-xs text-gray-500 border-t border-gray-700/60">
                  In {connection.mode === 'observe' ? 'Observe' : 'Off'} mode origin and bot failures are recorded here but still accepted.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
                <MailWarning className="w-4 h-4 text-gray-400" />
                <h3 className="text-sm font-semibold">Welcome emails</h3>
              </div>
              <dl className="grid grid-cols-3 text-center py-4">
                <div>
                  <dt className="text-xs text-gray-400">Sent</dt>
                  <dd className="text-xl font-semibold tabular-nums">{summary.welcome.sent.toLocaleString('en-AU')}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-400">Bounced</dt>
                  <dd className="text-xl font-semibold tabular-nums">{summary.welcome.bounced.toLocaleString('en-AU')}</dd>
                  <dd className="text-xs text-gray-500">{pct(summary.welcome.bounced, summary.welcome.sent)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-400">Spam complaints</dt>
                  <dd className="text-xl font-semibold tabular-nums">{summary.welcome.complained.toLocaleString('en-AU')}</dd>
                  <dd className="text-xs text-gray-500">{pct(summary.welcome.complained, summary.welcome.sent)}</dd>
                </div>
              </dl>
              <p className="px-4 pb-3 text-xs text-gray-500">
                Welcome emails pause automatically when more than 2% bounce or recipients mark them as spam.
              </p>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default WebsiteHealthPanel;
